import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, signInAnonymously as firebaseSignInAnonymously, User, UserCredential, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('https://www.googleapis.com/auth/drive.file');

// User data interface
export interface UserData {
  uid: string;
  email: string;
  displayName: string;
  photoURL?: string;
  stripeCustomerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  plan: 'free' | 'premium';
  createdAt: any;
  lastLoginAt: any;
  preferences?: {
    theme?: 'light' | 'dark';
    notifications?: boolean;
    cloudSyncEnabled?: boolean; // True if user has ever activated Cloud Sync
  };
}

// Sign in with Google
export const signInWithGoogle = async (): Promise<UserData | null> => {
  try {
    console.log('🔐 Starting Firebase Google sign-in...');
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    if (user) {
      console.log('✅ Google sign-in successful:', user.email);
      
      // Create or update user document in Firestore
      const userData = await createOrUpdateUser(user);
      return userData;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Firebase Google sign-in error:', error);
    throw error;
  }
};
// Sign in anonymously (for jam sessions without account)
export const signInAnonymously = async (): Promise<UserCredential> => {
  try {
    console.log('👤 Starting Firebase anonymous sign-in...');
    const result = await firebaseSignInAnonymously(auth);
    console.log('✅ Anonymous sign-in successful:', result.user.uid);
    return result;
  } catch (error) {
    console.error('❌ Firebase anonymous sign-in error:', error);
    throw error;
  }
};
// Create or update user document
export const createOrUpdateUser = async (user: User): Promise<UserData> => {
  const userRef = doc(db, 'users', user.uid);
  const userSnap = await getDoc(userRef);
  
  const now = serverTimestamp();
  
  if (userSnap.exists()) {
    // Update existing user's last login
    const existingData = userSnap.data() as UserData;
    await updateDoc(userRef, {
      lastLoginAt: now,
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL
    });
    
    console.log('📝 Updated existing user:', user.email);
    return {
      ...existingData,
      email: user.email || existingData.email,
      displayName: user.displayName || existingData.displayName,
      photoURL: user.photoURL || existingData.photoURL
    };
  } else {
    // Create new user document
    const newUserData: UserData = {
      uid: user.uid,
      email: user.email || '',
      displayName: user.displayName || 'User',
      photoURL: user.photoURL || '',
      plan: 'free',
      createdAt: now,
      lastLoginAt: now,
      preferences: {
        theme: 'light',
        notifications: true
      }
    };
    
    await setDoc(userRef, newUserData);
    console.log('🆕 Created new user:', user.email);
    
    // Push Google Ads conversion event for new user signup
    if (typeof window !== 'undefined' && (window as any).dataLayer) {
      (window as any).dataLayer.push({
        'event': 'user_signup',
        'user_id': user.uid
      });
      console.log('🎯 GTM Event: user_signup -', user.uid);
    }
    
    return newUserData;
  }
};

// Get user data
export const getUserData = async (uid: string): Promise<UserData | null> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data() as UserData;
    }
    
    return null;
  } catch (error) {
    console.error('❌ Error fetching user data:', error);
    return null;
  }
};

// Update user subscription
export const updateUserSubscription = async (
  uid: string, 
  updates: {
    stripeCustomerId?: string;
    subscriptionId?: string;
    subscriptionStatus?: string;
    plan?: 'free' | 'premium';
  }
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, updates);
    console.log('💳 Updated user subscription:', uid, updates);
  } catch (error) {
    console.error('❌ Error updating user subscription:', error);
    throw error;
  }
};

// Update user preferences (including cloudSyncEnabled)
export const updateUserPreferences = async (
  uid: string,
  preferences: Partial<UserData['preferences']>
): Promise<void> => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const existingData = userSnap.data() as UserData;
      const updatedPreferences = {
        ...existingData.preferences,
        ...preferences
      };
      await updateDoc(userRef, { preferences: updatedPreferences });
      console.log('⚙️ Updated user preferences:', uid, preferences);
    }
  } catch (error) {
    console.error('❌ Error updating user preferences:', error);
    throw error;
  }
};

// Get cloudSyncEnabled preference
export const getCloudSyncPreference = async (uid: string): Promise<boolean> => {
  try {
    const userData = await getUserData(uid);
    return userData?.preferences?.cloudSyncEnabled ?? false;
  } catch (error) {
    console.error('❌ Error getting cloudSync preference:', error);
    return false;
  }
};

// Set cloudSyncEnabled preference
export const setCloudSyncPreference = async (uid: string, enabled: boolean): Promise<void> => {
  await updateUserPreferences(uid, { cloudSyncEnabled: enabled });
  console.log(`☁️ Cloud Sync preference set to ${enabled} for user ${uid}`);
};

// Sign out
export const signOutUser = async (): Promise<void> => {
  try {
    await signOut(auth);
    console.log('👋 User signed out');
  } catch (error) {
    console.error('❌ Error signing out:', error);
    throw error;
  }
};

// Auth state listener
export const onAuthStateChange = (callback: (user: User | null) => void) => {
  return onAuthStateChanged(auth, callback);
};

// Get current user
export const getCurrentUser = (): User | null => {
  return auth.currentUser;
};